/**
 * Stripe Helper Utilities
 * 
 * This file contains utility functions for working with Stripe data
 * and formatting it for use in the application.
 */

/**
 * Format price from cents to dollars
 * @param {number} amountInCents - Price in cents
 * @param {string} currency - Currency code (default: 'usd')
 * @returns {string} Formatted price string
 */
export const formatPrice = (amountInCents, currency = 'usd') => {
  if (!amountInCents) return 'Custom';
  
  const amount = amountInCents / 100;
  const currencySymbols = {
    usd: '$',
    eur: '€',
    gbp: '£',
    cad: 'C$',
    aud: 'A$'
  };
  
  const symbol = currencySymbols[currency.toLowerCase()] || currency.toUpperCase();
  return `${symbol}${amount.toFixed(2)}`;
};

/**
 * Format price from dollars to cents
 * @param {number} amountInDollars - Price in dollars
 * @returns {number} Price in cents
 */
export const formatPriceToCents = (amountInDollars) => {
  return Math.round(amountInDollars * 100);
};

/**
 * Get plan type from plan name
 * @param {string} planName - Name of the plan
 * @returns {string} Plan type (starter, pro, advanced, enterprise)
 */
export const getPlanType = (planName) => {
  if (!planName) return 'default';
  
  const name = planName.toLowerCase();
  if (name.includes('starter')) return 'starter';
  if (name.includes('pro')) return 'pro';
  if (name.includes('advanced')) return 'advanced';
  if (name.includes('enterprise')) return 'enterprise';
  
  return 'default';
};

/**
 * Validate Stripe plan data
 * @param {Object} plan - Plan object to validate
 * @returns {Object} Validation result with isValid and errors
 */
export const validateStripePlan = (plan) => {
  const errors = [];
  
  if (!plan) {
    errors.push('Plan is required');
    return { isValid: false, errors };
  }
  
  if (!plan.stripePriceId && !plan.stripeProductId) {
    errors.push('Plan must have either stripePriceId or stripeProductId');
  }
  
  if (!plan.name) {
    errors.push('Plan name is required');
  }
  
  if (plan.price !== undefined && plan.price < 0) {
    errors.push('Plan price cannot be negative');
  }
  
  if (plan.tokens !== undefined && plan.tokens < 0) {
    errors.push('Plan tokens cannot be negative');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Create fallback plan data
 * @param {string} planType - Type of plan (starter, pro, advanced, enterprise)
 * @returns {Object} Fallback plan object
 */
export const createFallbackPlan = (planType) => {
  const fallbackPlans = {
    starter: {
      id: 'fallback-starter',
      name: 'NKENNEAi Starter Pack',
      price: 'US$10',
      amount: 1000,
      tokens: 100000,
      features: ['100,000 Tokens', '400,000 Characters'],
      buttonText: 'Select Plan',
      isActive: true
    },
    pro: {
      id: 'fallback-pro',
      name: 'NKENNEAi Pro Pack',
      price: 'US$50',
      amount: 5000,
      tokens: 550000,
      features: ['550,000 Tokens', '2,200,000 Characters'],
      buttonText: 'Select Plan',
      isActive: true
    },
    advanced: {
      id: 'fallback-advanced',
      name: 'NKENNEAi Advanced Pack',
      price: 'US$100',
      amount: 10000,
      tokens: 1200000,
      features: ['1,200,000 Tokens', '4,800,000 Characters'],
      buttonText: 'Select Plan',
      isActive: true
    },
    enterprise: {
      id: 'fallback-enterprise',
      name: 'NKENNEAi Enterprise',
      price: 'Custom',
      amount: null,
      tokens: null,
      features: ['Custom', 'Tailored for large scale usage'],
      buttonText: 'Contact Sales',
      isActive: true
    }
  };
  
  return fallbackPlans[planType] || fallbackPlans.starter;
};

/**
 * Check if plan is enterprise/custom pricing
 * @param {Object} plan - Plan object
 * @returns {boolean} True if plan is enterprise/custom
 */
export const isEnterprisePlan = (plan) => {
  if (!plan) return false;
  
  const name = plan.name?.toLowerCase() || '';
  return name.includes('enterprise') || 
         plan.price === 'Custom' || 
         plan.amount === null || 
         plan.amount === undefined;
};

/**
 * Get card background class based on plan type
 * @param {Object} plan - Plan object
 * @returns {string} CSS class for card background
 */
export const getCardBackgroundClass = (plan) => {
  const planType = getPlanType(plan?.name);
  
  const backgrounds = {
    starter: 'bg-[#FFF3C2]',
    pro: 'bg-[#FFE7D0]',
    advanced: 'bg-[#FFE7D0]',
    enterprise: 'bg-[#DCEDE8]',
    default: 'bg-gray-50'
  };
  
  return backgrounds[planType] || backgrounds.default;
};

/**
 * Validate Stripe configuration
 * @param {Object} config - Configuration object
 * @returns {Object} Validation result
 */
export const validateStripeConfig = (config) => {
  const errors = [];
  
  if (!config) {
    errors.push('Configuration is required');
    return { isValid: false, errors };
  }
  
  if (!config.stripePublishableKey) {
    errors.push('Stripe publishable key is required');
  }
  
  if (!config.apiBaseUrl) {
    errors.push('API base URL is required');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Check if plan has required Stripe metadata
 * @param {Object} plan - Plan object from Stripe
 * @returns {boolean} True if plan has required metadata
 */
export const hasRequiredMetadata = (plan) => {
  if (!plan) return false;
  
  const hasTokens = plan.metadata?.tokens || plan.tokens;
  const hasPlanName = plan.metadata?.planName || plan.name;
  
  return !!(hasTokens && hasPlanName);
};

/**
 * Extract tokens from plan metadata
 * @param {Object} plan - Plan object
 * @returns {number} Number of tokens, or 0 if not found
 */
export const extractTokensFromPlan = (plan) => {
  if (!plan) return 0;
  
  // Try different possible locations for tokens
  const tokens = plan.tokens || 
                 plan.metadata?.tokens || 
                 plan.product?.metadata?.tokens;
  
  return parseInt(tokens) || 0;
};

/**
 * Format plan for display with proper fallbacks
 * @param {Object} plan - Raw plan object from API
 * @returns {Object} Formatted plan object
 */
export const formatPlanForDisplay = (plan) => {
  if (!plan) return null;
  
  const tokens = extractTokensFromPlan(plan);
  const planName = plan.name || plan.metadata?.planName || 'Plan';
  
  return {
    ...plan,
    name: planName,
    tokens: tokens,
    displayTokens: tokens > 0 ? tokens.toLocaleString() : 'Unlimited',
    hasValidMetadata: hasRequiredMetadata(plan)
  };
};
